import React, { useState } from 'react';
import styles from './EditPrefRow.module.css';

export interface EditPrefRowOption<T extends string> {
    /** The value stored when this option is picked. */
    value: T;
    /** What the user sees in the option list. */
    label: string;
    /** Optional secondary line (e.g. "GMT+4" under "UAE Standard Time"). */
    sublabel?: string;
}

interface EditPrefRowProps<T extends string> {
    /** Top-line label (e.g. "Language", "Time zone"). */
    keyLabel: string;
    /** The currently selected value. */
    value: T;
    /** Available options. */
    options: ReadonlyArray<EditPrefRowOption<T>>;
    /** Called when the user picks a different option. */
    onChange: (value: T) => void;
    /**
     * Apply mode:
     *  - "instant" → picking an option applies + collapses immediately
     *    (no Save button; matches founder spec for Language)
     *  - "confirm" → Save / Cancel buttons in the expanded panel
     *    (matches founder spec for Time zone — gives a moment to back out)
     */
    applyMode?: 'instant' | 'confirm';
    /** Localized strings — required for both modes. */
    editLabel: string;
    cancelLabel?: string;
    saveLabel?: string;
    /** Disable the row entirely (e.g. while submitting). */
    disabled?: boolean;
}

/**
 * Fresha-parity preference row used in signup Step 1 + Settings → Preferences.
 *
 * Pattern (collapsed):
 *   ┌────────────────────────────────────────┐
 *   │ KEY                              [Edit]│
 *   │ Selected value                         │
 *   └────────────────────────────────────────┘
 *
 * Pattern (expanded, instant mode):
 *   ┌────────────────────────────────────────┐
 *   │ KEY                            [Cancel]│
 *   │ Selected value                         │
 *   ├────────────────────────────────────────┤
 *   │ ◉ English                              │  ← clicking applies + collapses
 *   │ ◯ العربية                              │
 *   └────────────────────────────────────────┘
 *
 * CR-2026-05-07a Phase 2B. Reused later in Settings → Preferences.
 */
function EditPrefRow<T extends string>({
    keyLabel,
    value,
    options,
    onChange,
    applyMode = 'instant',
    editLabel,
    cancelLabel = 'Cancel',
    saveLabel = 'Save',
    disabled = false,
}: EditPrefRowProps<T>) {
    const [isOpen, setIsOpen] = useState(false);
    const [draft, setDraft] = useState<T>(value);

    const selectedOption = options.find((o) => o.value === value);
    const selectedLabel = selectedOption?.label ?? value;

    const handleEditToggle = () => {
        if (disabled) return;
        if (isOpen) {
            // Cancel: discard draft
            setDraft(value);
            setIsOpen(false);
        } else {
            // Open
            setDraft(value);
            setIsOpen(true);
        }
    };

    const handleOptionClick = (next: T) => {
        if (applyMode === 'instant') {
            onChange(next);
            setDraft(next);
            setIsOpen(false);
        } else {
            setDraft(next);
        }
    };

    const handleSave = () => {
        onChange(draft);
        setIsOpen(false);
    };

    return (
        <div className={`${styles.row} ${isOpen ? styles.open : ''} ${disabled ? styles.disabled : ''}`}>
            <div className={styles.top}>
                <div className={styles.stack}>
                    <span className={styles.key}>{keyLabel}</span>
                    <span className={styles.val}>{selectedLabel}</span>
                </div>
                <button
                    type="button"
                    className={styles.editBtn}
                    onClick={handleEditToggle}
                    disabled={disabled}
                >
                    {isOpen ? cancelLabel : editLabel}
                </button>
            </div>

            {isOpen && (
                <div className={styles.body}>
                    {options.map((opt) => {
                        const isSelected =
                            applyMode === 'instant'
                                ? opt.value === value
                                : opt.value === draft;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                className={`${styles.option} ${isSelected ? styles.selected : ''}`}
                                onClick={() => handleOptionClick(opt.value)}
                            >
                                <span className={styles.optionLabel}>{opt.label}</span>
                                {opt.sublabel && (
                                    <span className={styles.optionSub}>{opt.sublabel}</span>
                                )}
                                {isSelected && (
                                    <span className={styles.check} aria-hidden="true">✓</span>
                                )}
                            </button>
                        );
                    })}

                    {applyMode === 'confirm' && (
                        <div className={styles.actions}>
                            <button type="button" className={styles.btnGhost} onClick={handleEditToggle}>
                                {cancelLabel}
                            </button>
                            <button type="button" className={styles.btnPrimary} onClick={handleSave}>
                                {saveLabel}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default EditPrefRow;
